import { ref } from "vue";
import instanceJWT from "../../api/apiJwt";
import type { Detail } from "../../models/Detail";

interface SuggestResponse {
  get_details?: {
    details: Detail[];
    brands_aliases: Record<string, string[]>;
    status: string;
  };
  vin?: Array<{
    vinCode: string;
    events: Record<string, unknown>;
  }>;
  plate?: Array<{
    plateNumber: string;
    events: Record<string, unknown>;
  }>;
  catalog?: Array<{
    name: string;
    events: Record<string, unknown>;
  }>;
  smart_search?: Array<{
    label: string;
    matched?: Record<string, string>;
    events: {
      click: {
        action: string;
        method: string;
        url: string;
        body?: Record<string, unknown>;
      };
    };
  }>;
  searchstring: string;
}

interface TabState {
  details: Detail[];
  brandsAliases: Record<string, string[]>;
  loading: boolean;
}

export { type SuggestResponse };

export function createSuggestActions(
  searches: Record<number, TabState>,
  ensureTabState: (tabId: number) => void,
) {
  const suggestLoading = ref(false);

  const fetchSuggest = async (tabId: number, searchstr: string): Promise<SuggestResponse | null> => {
    ensureTabState(tabId);
    const tab = searches[tabId];
    suggestLoading.value = true;

    try {
      const response = await instanceJWT.get(`eplex/lib/suggest1?q=${encodeURIComponent(searchstr)}`);

      let data = null;

      if (response.data?.data?.data) {
        data = response.data.data.data;
      } else if (response.data?.data) {
        data = response.data.data;
      } else if (response.data) {
        data = response.data;
      }

      if (!data) {
        return null;
      }

      if (data.get_details?.details) {
        tab.details.splice(0, tab.details.length, ...data.get_details.details);
        tab.brandsAliases = { ...(data.get_details.brands_aliases ?? {}) };
      }

      return data;
    } catch (error) {
      console.error("Fetch suggest error:", error);
      return null;
    } finally {
      suggestLoading.value = false;
    }
  };

  const fetchDetails = async (tabId: number, searchstr: string): Promise<void> => {
    ensureTabState(tabId);
    const tab = searches[tabId];
    tab.loading = true;
    try {
      const response = await instanceJWT.post("eplex/lib/get_details", { brands_aliases: true, searchstr });
      const data = response.data.data;
      if (data.status === "ok") {
        tab.details.splice(0, tab.details.length, ...data.details);
        tab.brandsAliases = { ...data.brands_aliases };
      } else {
        tab.details.splice(0, tab.details.length);
        tab.brandsAliases = {};
      }
    } catch (error) {
      console.error("Fetching details error: ", error);
      tab.details.splice(0, tab.details.length);
      tab.brandsAliases = {};
    } finally {
      tab.loading = false;
    }
  };

  const clearDetails = (tabId: number): void => {
    if (searches[tabId]) {
      searches[tabId].details = [];
      searches[tabId].brandsAliases = {};
    }
  };

  return { suggestLoading, fetchSuggest, fetchDetails, clearDetails };
}
